import React, { useState } from 'react';
import Header from '../../component/header/header';
import CustomInput from '../../component/CustomInput/CustomInput';
import { FaPlus } from 'react-icons/fa';
import Button from '../../component/button/button';
import { useNavigate } from 'react-router-dom';
import { saveTeacherData } from '../../api/teacher';
import { TeacherValidation } from './TeacherValidation';
import type { FormInputs, ErrorBags } from './TeacherInterface';
import ProfileImageUpload from '../../utils/ProfileImageUpload';
import axios from 'axios';
import { successMessage, errorMessageShow } from '../../utils/Helper';

export default function CreateTeacher() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<FormInputs>({
    id: '',
    first_name: '',
    last_name: '',
    address: '',
    phone_number: '',
    email: '',
    password: '',
    confirm_password: '',
    profile_photo: null,
  });

  const [errorBags, setErrorBags] = useState<ErrorBags>({
    id: '',
    first_name: '',
    last_name: '',
    address: '',
    phone_number: '',
    email: '',
    password: '',
    confirm_password: '',
    profile_photo: '',
  });

  const [submitStatus, setSubmitStatus] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);

  const accessToken = localStorage.getItem('access_token');

  if (!accessToken) {
    errorMessageShow('You are not logged in or the session has expired');
    navigate('/login');
  }

  const initialFormData: FormInputs = {
    first_name: '',
    last_name: '',
    address: '',
    phone_number: '',
    email: '',
    password: '',
    confirm_password: '',
    profile_photo: null,
  };

  const inputChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const errorMessage = TeacherValidation(name, value, formData);
    setErrorBags(prev => ({ ...prev, [name]: errorMessage }));
    setFormData({ ...formData, [name]: value });
  };

  const inputBlurHandler = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const errorMessage = TeacherValidation(name, value, formData);
    setErrorBags(prevErrors => ({
      ...prevErrors,
      [name]: errorMessage,
    }));
  };

  const validateForm = () => {
    let formIsValid = true;
    let errors = { ...errorBags };

    (Object.keys(formData) as (keyof FormInputs)[]).forEach(field => {
      if (field === 'profile_photo') {
        if (formData.profile_photo && formData.profile_photo !== '') {
          if (!formData.profile_photo) {
            formIsValid = false;
            errors[field] = 'Profile photo is required.';
          } else {
            errors[field] = '';
          }
        }
      } else {
        const errorMessage = TeacherValidation(
          field,
          formData[field] as string,
          formData
        );
        if (errorMessage) {
          formIsValid = false;
          errors[field] = errorMessage;
        } else {
          errors[field] = '';
        }
      }
    });
    setErrorBags(errors);
    return formIsValid;
  };

  const onSubmitHandler = async () => {
    const isValid = validateForm();
    if (!isValid) {
      errorMessageShow('Please fix the form errors before submitting.');
      return;
    }
    setSubmitStatus(true);
    setLoading(true);
    const formDataToSubmit = new FormData();
    formDataToSubmit.append('first_name', formData.first_name);
    formDataToSubmit.append('last_name', formData.last_name);
    formDataToSubmit.append('address', formData.address);
    formDataToSubmit.append('phone_number', formData.phone_number);
    formDataToSubmit.append('email', formData.email);
    formDataToSubmit.append('password', formData.password);

    if (formData.profile_photo) {
      formDataToSubmit.append('profile_photo', formData.profile_photo); // Append the file here
    }

    try {
      const submitTeacher = await saveTeacherData(formDataToSubmit);
      console.log(submitStatus);
      if (submitTeacher) {
        successMessage('Teacher added successfully!.');
        resetFormData();
        navigate('/teachers');
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const errorMessage =
          error.response?.data?.message || 'An unexpected error occurred.';
        errorMessageShow(errorMessage);
        setErrorBags(prevErrors => ({
          ...prevErrors,
          email: errorMessage,
        }));
        return;
      }
    } finally {
      setSubmitStatus(false);
      setLoading(false);
    }
  };

  const resetFormData = () => {
    setFormData(initialFormData);
    setErrorBags({
      first_name: '',
      last_name: '',
      address: '',
      phone_number: '',
      email: '',
      password: '',
      confirm_password: '',
    });
  };

  const handleNavigateBack = () => {
    navigate(-1);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files ? e.target.files[0] : null;
    setFormData(prevData => ({
      ...prevData,
      profile_photo: file,
    }));
  };

  return (
    <div className="content">
      <Header
        pageTitle="Add Teacher"
        buttonText={loading ? 'Submitting...' : 'Add Teacher'}
        buttonIcon={<FaPlus />}
        extraButton={<Button text="Close" onClick={handleNavigateBack} />}
        onClick={onSubmitHandler}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
        <form
          onSubmit={onSubmitHandler}
          className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-white p-4 rounded shadow-md"
        >
          <div className="col-span-2">
            <ProfileImageUpload
              inputChangeHandler={handleFileChange}
              setFormData={setFormData}
              setErrorBags={setErrorBags}
            />
            {errorBags.profile_photo && (
              <p className="text-red-500 text-xs mt-1">
                {errorBags.profile_photo}
              </p>
            )}
          </div>

          {/* First Name */}
          <div className="col-span-1">
            <label htmlFor="first_name" className="text-sm">
              First Name <span className="text-red-500">*</span>
            </label>
            <CustomInput
              placeholder="First name"
              value={formData.first_name}
              name="first_name"
              onChange={inputChangeHandler}
              onBlur={inputBlurHandler}
            />
            {errorBags.first_name && (
              <p className="text-red-500 text-xs mt-1">
                {errorBags.first_name}
              </p>
            )}
          </div>

          {/* Last Name */}
          <div className="col-span-1">
            <label className="text-sm" htmlFor="last_name">
              Last Name <span className="text-red-500">*</span>
            </label>
            <CustomInput
              placeholder="Last name"
              value={formData.last_name}
              name="last_name"
              onChange={inputChangeHandler}
              onBlur={inputBlurHandler}
            />
            {errorBags.last_name && (
              <p className="text-red-500 text-xs mt-1">{errorBags.last_name}</p>
            )}
          </div>

          {/* Address */}
          <div className="col-span-1 md:col-span-2">
            <label className="text-sm" htmlFor="address">
              Address
            </label>
            <CustomInput
              placeholder="Address"
              value={formData.address}
              name="address"
              onChange={inputChangeHandler}
              onBlur={inputBlurHandler}
            />
            {errorBags.address && (
              <p className="text-red-500 text-xs mt-1">{errorBags.address}</p>
            )}
          </div>

          {/* Phone Number */}
          <div className="col-span-1">
            <label className="text-sm" htmlFor="phone_number">
              Phone Number
            </label>
            <CustomInput
              placeholder="Phone number"
              value={formData.phone_number}
              name="phone_number"
              onChange={inputChangeHandler}
              onBlur={inputBlurHandler}
            />
            {errorBags.phone_number && (
              <p className="text-red-500 text-xs mt-1">
                {errorBags.phone_number}
              </p>
            )}
          </div>

          {/* Email */}
          <div className="col-span-1">
            <label className="text-sm" htmlFor="email">
              Email Address <span className="text-red-500">*</span>
            </label>
            <CustomInput
              placeholder="Email address"
              value={formData.email}
              name="email"
              onChange={inputChangeHandler}
              onBlur={inputBlurHandler}
            />
            {errorBags.email && (
              <p className="text-red-500 text-xs mt-1">{errorBags.email}</p>
            )}
          </div>

          {/* Password */}
          <div className="col-span-1">
            <label className="text-sm" htmlFor="password">
              Password <span className="text-red-500">*</span>
            </label>
            <CustomInput
              placeholder="Password"
              value={formData.password}
              name="password"
              type="password"
              onChange={inputChangeHandler}
              onBlur={inputBlurHandler}
            />
            {errorBags.password && (
              <p className="text-red-500 text-xs mt-1">{errorBags.password}</p>
            )}
          </div>

          {/* Confirm Password */}
          <div className="col-span-1">
            <label className="text-sm" htmlFor="confirm_password">
              Confirm Password <span className="text-red-500">*</span>
            </label>
            <CustomInput
              placeholder="Confirm password"
              value={formData.confirm_password}
              name="confirm_password"
              type="password"
              onChange={inputChangeHandler}
              onBlur={inputBlurHandler}
            />
            {errorBags.confirm_password && (
              <p className="text-red-500 text-xs mt-1">
                {errorBags.confirm_password}
              </p>
            )}
          </div>

          {/* Submit Button */}
          <div className="col-span-1 md:col-span-2 mt-4 text-right">
            <Button
              text={loading ? 'Submitting...' : 'CREATE'}
              buttonType="button"
              onClick={() => onSubmitHandler()}
              style={{
                height: 45,
                fontSize: 14,
              }}
            />
          </div>
        </form>
      </div>
    </div>
  );
}
